/**
 * Subscription billing rules for upgrade / purchase UI.
 *
 * Two layers (must stay separate):
 * 1. Stripe billing ended → canceled status / billingCancellation → createNewSubscription, no partner pricing
 * 2. App access → helpers.isSubscriptionValid() until current_period_end, except trial/freemium immediate cancel policies
 */

import moment from "moment";
import { pickSubscriptionsForValidation, isSubscriptionValid } from "../services/helpers";
import { BILLING_RESTRICTED_ALLOWED_PATHS } from "../services/access/constants";

/** companyUser may visit these when substatus is expired and/or billing is restricted. */
const COMPANY_USER_SUBSCRIPTION_LIMITED_EXEMPT_PATHS = new Set([
  ...BILLING_RESTRICTED_ALLOWED_PATHS,
  "/peoples/manageEmp",
  "/peoples/inactiveEmp",
]);
const TRIAL_ONLY_EXEMPT_PATHS = new Set([
  "/account/upgrade",
  "/account/companySubscription",
]);
const TRIAL_PLAN_PRODUCT_IDS = new Set([
  "prod_SoFkXIV3TK8yY1",
  "prod_Sk8QqRuvoQLkQK",
  "prod_Sk8YQ7MHqCIMSV",
]);
const TRIAL_PRICE_IDS = new Set([
  "price_1RsdExHPX5Uwv3eVvXeq6n3a",
  "price_1Roe9PH54HzvHwSdFEwomMT8",
  "price_1RoeHcHG9KfCFcBtNO24WZi1",
]);

/** Paths where plan pkgAccess checks are skipped when subscription is limited (checkout UI). */
export const COMPANY_USER_BILLING_PKG_ACCESS_PATHS = new Set([
  "/account/upgrade",
  "/account/companySubscription",
]);

/** Same end-of-month display as CompanySubscription canceled access message. */
export function formatCanceledAccessEndDate(periodEndSeconds) {
  const ts = Number(periodEndSeconds);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  return moment.unix(ts).endOf("month").format("MM/DD/YYYY");
}

export function isFoundingPartnerPromoCode(promoCode) {
  return typeof promoCode === "string" && promoCode.trim().toLowerCase().startsWith("fp");
}

function readStoredPromoCode(profile) {
  const raw = profile?.userPromoCode ?? profile?.companyData?.userPromoCode;
  if (typeof raw === "string") return raw.trim();
  if (Array.isArray(raw) && raw.length) return String(raw[0] ?? "").trim();
  return "";
}

/** Stored profile promo is ignored for partner UI/checkout until a new subscription is purchased. */
export function isStoredPromoStale(profile) {
  if (!readStoredPromoCode(profile)) return false;
  return resolveSubscriptionBillingState(profile).shouldCreateNewSubscription;
}

export function resolveFoundingPartnerPromoCode(profile) {
  if (isStoredPromoStale(profile)) return "";
  return readStoredPromoCode(profile);
}

/** Promo passed into packageAccess() — null while canceled + awaiting repurchase. */
export function getProfilePromoForPackageAccess(profile) {
  if (isStoredPromoStale(profile)) return null;
  const code = readStoredPromoCode(profile);
  return code || null;
}

function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

function pickSubscriptionRecord(profile) {
  const details = profile?.subscriptionDetails;
  const fromList = profile?.subscriptions?.data?.[0];
  if (details?.id || details?.status) return details;
  return fromList || details || null;
}

/** Stripe/Firestore signals that the paid sub is ended — new checkout must not reuse it. */
export function isStripeBillingEnded(profile) {
  if (!profile) return false;
  if (profile.billingCancellation?.canceledAt || profile.billingCancellation?.policyType) {
    return true;
  }

  const billingState = profile.billingState || {};
  if (normalizeStatus(billingState.subscriptionStatus) === "canceled") return true;

  const subscription = pickSubscriptionRecord(profile);
  return normalizeStatus(subscription?.status) === "canceled";
}

/** User may still use the app until period end (or 30-day money-back window). */
export function hasAppAccessAfterCancel(profile) {
  const { subscription, fallback } = pickSubscriptionsForValidation(profile || {});
  return isSubscriptionValid(subscription, fallback, profile);
}

/** Route whitelist only after paid access window has ended (not immediately on cancel). */
export function shouldEnforceBillingRouteRestriction(profile) {
  if (isTrialCanceledImmediate(profile)) return true;
  if (!profile?.billingAccessRestricted) return false;
  return !hasAppAccessAfterCancel(profile);
}

export function isCompanyUserSubscriptionLimitedExemptPath(path) {
  return COMPANY_USER_SUBSCRIPTION_LIMITED_EXEMPT_PATHS.has(path);
}

export function isCompanyUserTrialCanceledExemptPath(path) {
  return TRIAL_ONLY_EXEMPT_PATHS.has(path);
}

function hasTrialPlanIdentifier(profile) {
  const subscription = pickSubscriptionRecord(profile);
  const planProductId = String(
    subscription?.plan?.product ||
      subscription?.items?.data?.[0]?.price?.product ||
      profile?.subscriptionDetails?.plan?.product ||
      profile?.subscriptionDetails?.price?.planid ||
      profile?.prdId ||
      ""
  ).trim();
  const priceId = String(
    subscription?.items?.data?.[0]?.price?.id ||
      profile?.subscriptionDetails?.price?.id ||
      ""
  ).trim();
  return TRIAL_PLAN_PRODUCT_IDS.has(planProductId) || TRIAL_PRICE_IDS.has(priceId);
}

export function isTrialCanceledImmediate(profile) {
  const subscription = pickSubscriptionRecord(profile);
  const status = normalizeStatus(
    subscription?.status || profile?.billingState?.subscriptionStatus
  );
  if (status !== "canceled") return false;
  const cancelPolicy = normalizeStatus(profile?.billingCancellation?.policyType);
  if (
    cancelPolicy === "trial_expired" ||
    cancelPolicy === "trial_cancel_immediate"
  ) {
    return true;
  }
  return hasTrialPlanIdentifier(profile);
}

/** companyUser: block app routes when trial/paid access ended (substatus) or post-cancel restriction applies. */
export function isCompanyUserSubscriptionOrBillingLimited(substatus, profile) {
  return (
    substatus === true ||
    shouldEnforceBillingRouteRestriction(profile || {})
  );
}

/** companyUser may always open Plans/billing/account settings even when substatus is expired. */
export function isCompanyUserSubscriptionExpiredExemptPath(path) {
  const routePath = String(path || "");
  if (!routePath) return false;
  if (COMPANY_USER_SUBSCRIPTION_LIMITED_EXEMPT_PATHS.has(routePath)) return true;
  return BILLING_RESTRICTED_ALLOWED_PATHS.includes(routePath);
}

/**
 * Centralized route-restriction context for company users.
 * Keep all trial/paid/freemium restriction decisions in one place.
 */
export function getCompanyUserRestrictionContext(substatus, profile, path) {
  const routePath = String(path || "");
  const trialCanceledImmediate = isTrialCanceledImmediate(profile || {});
  const subscriptionLimited = isCompanyUserSubscriptionOrBillingLimited(
    substatus,
    profile || {}
  );
  const limitedExemptPath = trialCanceledImmediate
    ? isCompanyUserTrialCanceledExemptPath(routePath)
    : isCompanyUserSubscriptionLimitedExemptPath(routePath);
  const expiredExemptPath = trialCanceledImmediate
    ? isCompanyUserTrialCanceledExemptPath(routePath)
    : isCompanyUserSubscriptionExpiredExemptPath(routePath);

  return {
    trialCanceledImmediate,
    subscriptionLimited,
    limitedExemptPath,
    expiredExemptPath,
  };
}

/**
 * Latest subscription + billing flags used by upgrade/purchase UI and API calls.
 */
export function resolveSubscriptionBillingState(profile) {
  const subscription = pickSubscriptionRecord(profile);
  const billingState = profile?.billingState || {};

  const status =
    normalizeStatus(subscription?.status) ||
    normalizeStatus(billingState?.subscriptionStatus);

  const cancelAtPeriodEnd = Boolean(
    subscription?.cancel_at_period_end ||
    billingState?.cancelAtPeriodEnd
  );

  const isFreemiumBilling =
    normalizeStatus(billingState?.planType) === "freemium";

  const stripeBillingEnded = isStripeBillingEnded(profile);
  const isCanceled = status === "canceled" || stripeBillingEnded;
  const isScheduledCancel =
    !stripeBillingEnded &&
    cancelAtPeriodEnd &&
    (status === "active" || status === "trialing");

  const subscriptionId = subscription?.id || null;
  const hasActivePaidSubscription =
    Boolean(subscriptionId) &&
    !stripeBillingEnded &&
    !isScheduledCancel &&
    !isFreemiumBilling &&
    (status === "active" || status === "trialing" || status === "past_due");

  const shouldCreateNewSubscription =
    stripeBillingEnded || !subscriptionId || isFreemiumBilling;

  const hasGraceAccess = hasAppAccessAfterCancel(profile);
  const showCanceledAccessBanner =
    hasGraceAccess &&
    (stripeBillingEnded || isScheduledCancel || profile?.billingAccessRestricted === true);

  const periodEndSeconds = Number(
    subscription?.current_period_end ||
    billingState?.currentPeriodEnd ||
    profile?.billingCancellation?.effectivePeriodEnd
  );
  const accessValidThrough = formatCanceledAccessEndDate(periodEndSeconds);

  return {
    subscription,
    subscriptionId,
    status,
    cancelAtPeriodEnd,
    isFreemiumBilling,
    isCanceled,
    isScheduledCancel,
    stripeBillingEnded,
    hasActivePaidSubscription,
    shouldCreateNewSubscription,
    showCanceledAccessBanner,
    hasAppAccessAfterCancel: hasAppAccessAfterCancel(profile),
    accessValidThrough,
  };
}

export function hasStoredFoundingPartnerCode(profile) {
  return isFoundingPartnerPromoCode(resolveFoundingPartnerPromoCode(profile));
}

/** Matches backend isPartnerBillingUser — not subscription schedules. */
export function isPartnerBillingProfile(profile = {}) {
  const storedPromo = readStoredPromoCode(profile);
  const legacyPromo = String(profile?.promocode || profile?.promoCode || "").trim();
  if (isFoundingPartnerPromoCode(storedPromo) || isFoundingPartnerPromoCode(legacyPromo)) {
    return true;
  }
  return profile?.subscriptionDetails?.isFullDiscount === true;
}

/** Frontend guard before transitionToFreemium (same rules as the cloud function). */
export function shouldBlockFreemiumTransition(profile, manualCode, isFullDiscount) {
  if (isManualPartnerPromoApplied(manualCode, isFullDiscount)) return true;
  return isPartnerBillingProfile(profile);
}

export function shouldApplyStoredPartnerPricing(profile) {
  const billing = resolveSubscriptionBillingState(profile);
  if (!hasStoredFoundingPartnerCode(profile)) return false;
  if (billing.shouldCreateNewSubscription) return false;
  return billing.hasActivePaidSubscription;
}

export function getEffectivePromoCodeForCheckout(profile, manualCode, options = {}) {
  const { manualApplied = false } = options;
  const trimmedManual = typeof manualCode === "string" ? manualCode.trim() : "";
  const billing = resolveSubscriptionBillingState(profile);

  // After cancel: never auto-apply stored profile promo; manual Apply (including new FP) is allowed.
  if (billing.shouldCreateNewSubscription) {
    if (manualApplied && trimmedManual) {
      return trimmedManual.toUpperCase();
    }
    return "";
  }

  if (manualApplied && trimmedManual) {
    return trimmedManual.toUpperCase();
  }

  if (shouldApplyStoredPartnerPricing(profile)) {
    const stored = resolveFoundingPartnerPromoCode(profile);
    if (stored) return stored.toUpperCase();
  }

  return manualApplied && trimmedManual ? trimmedManual.toUpperCase() : "";
}

export function isManualPartnerPromoApplied(manualCode, isFullDiscount) {
  return Boolean(isFullDiscount && isFoundingPartnerPromoCode(manualCode));
}

export function shouldUsePartnerCheckoutFlow(profile, manualCode, isFullDiscount) {
  const billing = resolveSubscriptionBillingState(profile);
  if (billing.shouldCreateNewSubscription) return false;

  if (isManualPartnerPromoApplied(manualCode, isFullDiscount)) {
    return billing.hasActivePaidSubscription;
  }
  return shouldApplyStoredPartnerPricing(profile);
}
